// frontend/src/lib/notificationSound.js
//
// Audible alert for the notification bell — a synthesised chime via the Web
// Audio API. NO audio file: an <audio src> or fetch would need a CDN the
// artifact/CSP setup doesn't allow, and a data-URI WAV is bulky. A couple
// of oscillator beeps cost nothing and are tuned to cut through a noisy
// front desk without being shrill.
//
// The point of the sound is that someone NOT looking at the screen still
// notices — so it plays regardless of tab focus, on every rise in the open
// notification count. A per-browser mute toggle (localStorage) lets a quiet
// office turn it off; default is ON.
//
// Browsers suspend an AudioContext created before the first user gesture.
// primeAudio() is wired to a one-time pointer/key listener in
// NotificationBell so the context is running by the time the first alert
// fires; if it still isn't (no interaction yet), playAlert() resumes it
// best-effort and simply no-ops if the browser refuses.

const MUTE_KEY = 'talit-notif-sound-muted';

let ctx = null;

function getCtx() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) {
    try { ctx = new AC(); } catch { return null; }
  }
  return ctx;
}

export function isSoundMuted() {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSoundMuted(muted) {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* private mode / storage disabled — the toggle just won't persist */
  }
}

// Call from a user-gesture handler so the context is unlocked ahead of the
// first real alert. Safe to call repeatedly.
export function primeAudio() {
  const c = getCtx();
  if (c && c.state === 'suspended') {
    c.resume().catch(() => {});
  }
}

// One short tone. `when` is an absolute AudioContext time.
function tone(c, { freq, start, duration, gain = 0.14, type = 'sine' }) {
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  // Quick fade in/out so it doesn't click.
  g.gain.setValueAtTime(0, start);
  g.gain.linearRampToValueAtTime(gain, start + 0.015);
  g.gain.setValueAtTime(gain, start + duration - 0.04);
  g.gain.linearRampToValueAtTime(0, start + duration);
  osc.connect(g);
  g.connect(c.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

// severity: 'critical' plays a more insistent triple-beep, slightly louder;
// anything else plays a gentle two-note rise.
export function playAlert(severity = 'info') {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') {
    c.resume().catch(() => {});
  }
  const t0 = c.currentTime + 0.01;

  if (severity === 'critical') {
    tone(c, { freq: 880, start: t0, duration: 0.16, gain: 0.2, type: 'square' });
    tone(c, { freq: 880, start: t0 + 0.22, duration: 0.16, gain: 0.2, type: 'square' });
    tone(c, { freq: 1175, start: t0 + 0.44, duration: 0.24, gain: 0.2, type: 'square' });
  } else {
    tone(c, { freq: 660, start: t0, duration: 0.13 });
    tone(c, { freq: 880, start: t0 + 0.15, duration: 0.2 });
  }
}
